import {Icon} from '@blueprintjs/core';
import {IconNames} from '@blueprintjs/icons';
import {when} from 'mobx';
import {observer} from 'mobx-react';
import {useObserver} from 'mobx-react-lite';
import React from 'react';
import {Link} from 'react-router-dom';
import {LoadingIndicator} from '../components/LoadingIndicator';
import {AppDetailStore} from '../stores/AppDetailStore';
import {AppDetailTabs} from './AppDetail/AppDetailTabs';
import {AppInfoCard} from './AppDetail/AppInfoCard';
import styles from './AppDetailPage.module.sass';

interface Props {
  appId: string;
  version?: string;
  hideBreadcrumbs?: boolean;
}

export const AppDetailPage = observer(function AppDetailPage(props: Props) {
  // Lazy useState init creates the store once per mount.
  const [detailStore] = React.useState(() => new AppDetailStore(props.appId, props.version));

  React.useEffect(() => {
    // when(...) returns a disposer; returning it from useEffect cleans up on unmount.
    return when(() => !detailStore.loading, () => detailStore.sendJumbeEvent('view'));
  }, [detailStore]);

  if (detailStore.loading) {
    return (
      <LoadingIndicator />
    );
  } else if (detailStore.status === 404) {
    return (
      <div className={styles.error}>
        <Icon icon={IconNames.PATH_SEARCH} className={styles.icon} iconSize={40} />
        <div className={styles.text}>Just a 404, nothing to see here.</div>
        <Link to="/">Back to App Directory</Link>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {!props.hideBreadcrumbs && <Breadcrumbs />}
      <div className={styles.main}>
        <AppInfoCard detailStore={detailStore} />
        {detailStore.scopeLoading
          ? <LoadingIndicator />
          : <AppDetailTabs detailStore={detailStore} />
        }
      </div>
    </div>
  );
});

// Can't use the stock blueprint breadcrumbs because we need to use the react-router Link
const Breadcrumbs = () => {
  return useObserver(() => {
    return (
      <div className={styles.breadcrumbs}>
        <Icon icon={IconNames.CHEVRON_LEFT} className={styles.chevron} />
        <Link to="/">All Apps</Link>
      </div>
    );
  });
};
